import {
  createContext,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { useKey } from 'react-use'

import { MediaElement } from '@/components/base/media'
import SVGIcon from '@/components/base/svg-icon'
import Typography from '@/components/base/typography'
import { useLoaderStore } from '@/store/loader'
import { heroCarouselAutoplayInterval } from '@/constants'

import { cn } from '@/lib/utils'
import { WPLink } from '@/types'

export type Slide = {
  pretitle: string
  title: string
  subtitle: string
  paragraph: string
  button: WPLink
  media: MediaElement
  show_bio_label: boolean
}

export type Carousel = {
  currentSlide: Slide
  currentSlideIndex: number
  gotoNextSlide: () => void
  gotoPrevSlide: () => void
}

export type CarouselProviderProps = PropsWithChildren<{
  slides: Slide[]
  use_carousel?: boolean
}>

export const CarouselContext = createContext<Carousel | undefined>(undefined)

export const CarouselProvider = ({
  slides = [],
  use_carousel,
  children
}: CarouselProviderProps) => {
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0)
  const currentSlide = useMemo(
    () => slides[currentSlideIndex],
    [slides, currentSlideIndex]
  )

  const needsAutoplay = useRef(true)
  const needsCarousel = useMemo(
    () => use_carousel && slides && slides.length > 1,
    [slides, use_carousel]
  )

  const autoplayTimeout = useRef<any>(null)

  const clearAutoplayTimeout = useCallback((clearAutoplay = true) => {
    needsAutoplay.current = !clearAutoplay
    clearAutoplay && clearTimeout(autoplayTimeout.current)
  }, [])

  const gotoPrevSlide = useCallback(
    (clearAutoplay?: boolean) => {
      clearAutoplayTimeout(clearAutoplay)
      setCurrentSlideIndex((prev) => (prev - 1 + slides.length) % slides.length)
    },
    [slides, clearAutoplayTimeout]
  )

  const gotoNextSlide = useCallback(
    (clearAutoplay?: boolean) => {
      clearAutoplayTimeout(clearAutoplay)
      setCurrentSlideIndex((prev) => (prev + 1) % slides.length)
    },
    [slides, clearAutoplayTimeout]
  )

  useKey('ArrowLeft', () => gotoPrevSlide(), { event: 'keydown' }, [
    gotoPrevSlide
  ])
  useKey('ArrowRight', () => gotoNextSlide(), { event: 'keydown' }, [
    gotoNextSlide
  ])

  useEffect(() => {
    const unsubscribe = useLoaderStore.subscribe(
      (state) => [state.heroLoaded, state.heroCoverLoaded],
      ([heroLoaded, heroCoverLoaded], [prevHeroLoaded]) => {
        clearTimeout(autoplayTimeout.current)
        if (
          heroLoaded &&
          heroCoverLoaded &&
          needsCarousel &&
          needsAutoplay.current
        ) {
          autoplayTimeout.current = setTimeout(
            () => gotoNextSlide(false),
            heroCarouselAutoplayInterval + (!prevHeroLoaded ? 2000 : 0)
          )
        }
      }
    )

    return () => {
      clearTimeout(autoplayTimeout.current)
      unsubscribe()
    }
  }, [needsCarousel, gotoNextSlide])

  const contextValue = useMemo(
    () => ({
      currentSlide,
      currentSlideIndex,
      gotoPrevSlide,
      gotoNextSlide
    }),
    [currentSlide, currentSlideIndex, gotoPrevSlide, gotoNextSlide]
  )

  return (
    <CarouselContext.Provider value={contextValue}>
      {children}
      {needsCarousel ? (
        <div
          className={cn([
            'absolute bottom-[4.8rem] left-[4rem] z-10 flex items-center space-x-[1.4rem]',
            'lg:left-[8rem]'
          ])}
          data-name="controls"
        >
          <Typography
            variant="small"
            paragraph
            className="font-bold uppercase text-text-overlay-heading-light"
          >
            {`${currentSlideIndex + 1} / ${slides.length}`}
          </Typography>

          <button
            type="button"
            className="-m-[1rem] p-[1rem]"
            onClick={() => gotoPrevSlide()}
          >
            <SVGIcon
              symbol="prev-arrow-carousel"
              className={cn([
                'h-[1rem] w-[2.4rem] fill-surface-primay-invert-light dark:fill-surface-primay-invert-dark',
                'transition-transform duration-200 ease-out-cubic',
                'hover:-translate-x-1'
              ])}
            />
          </button>

          <button
            type="button"
            className="-m-[1rem] p-[1rem]"
            onClick={() => gotoNextSlide()}
          >
            <SVGIcon
              symbol="next-arrow-carousel"
              className={cn([
                'h-[1rem] w-[8rem] fill-surface-primay-invert-light dark:fill-surface-primay-invert-dark',
                'transition-transform duration-200 ease-out-cubic',
                'hover:translate-x-1'
              ])}
            />
          </button>
        </div>
      ) : null}
    </CarouselContext.Provider>
  )
}

export default CarouselProvider
